/**
 * ShareDocumentDialog — Per-doc ACL management UI (Wave 2 finish, b51).
 *
 * Backed by /api/v1/library/acl. Three tabs (Direct / Group / Role) for
 * adding grants, plus an Effective ACL panel showing existing grants
 * with inheritance badges. Each existing grant has a remove button.
 *
 * Permissions:
 *   - Add grant     → POST /documents/:id/grants     (share:library)
 *   - Remove grant  → DELETE /documents/:id/grants/:grantId
 *
 * Note on inheritance: doc grants and folder grants both surface here.
 * Folder grants render with an "inherited" badge; the remove button is
 * disabled on inherited rows because revoking has to happen on the
 * folder (use ShareFolderDialog for that).
 */

import { useState, useMemo } from 'react';
import { Loader2, Share2, X, Trash2, Info, Plus } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useDepartments } from '@/lib/use-users';
import { ShareRolePicker, useShareRoleTab } from './share-role-picker';
// THE display rule for a person (LB6-T4-BF-05): the picker and the
// current-access list name people as every Library tab does.
import { userPickerLabel, type NamedUser } from '@argusai/shared/schemas/user-display';

// ─── Types (mirror routes/v1/library/acl.ts response shapes) ───────────────

// 'department' added by #176 / bAUTO_1101. Matches every user who holds a
// MEMBERSHIP of the grant's subject_department_id — a row in
// `user_department_memberships`, read through the one resolver in
// packages/api/src/policy/principal.ts. It read `users.department_id` until
// B2a/B2b moved it; c30 dropped that column, and a person is in one or more
// departments (B2b-T3), so a grant can match somebody whose other departments
// this document says nothing about.
type AclSubjectKind = 'user' | 'group' | 'role' | 'department';
type AclPermission = 'view' | 'edit' | 'delete' | 'share';

interface AclEntry {
  grantId: string;
  subjectKind: AclSubjectKind;
  subjectUserId: string | null;
  subjectGroupId: string | null;
  subjectRole: string | null;
  subjectDepartmentId: string | null;
  permission: AclPermission;
  grantedBy: string | null;
  grantedAt: string;
  expiresAt: string | null;
  reason: string | null;
  source: 'direct' | 'inherited_folder';
  /** A `user` share's subject, named by the server (LB6-T4-R5); null otherwise. */
  subjectUser: NamedUser | null;
}

interface EffectiveAclResponse {
  direct: AclEntry[];
  inheritedFromFolder: AclEntry[];
  effective: AclEntry[];
}

interface UserListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface GroupListItem {
  id: string;
  name: string;
}

interface ShareDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentTitle?: string;
}

const PERMISSIONS: Array<{ value: AclPermission; label: string }> = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
  { value: 'delete', label: 'Delete' },
  { value: 'share', label: 'Share' },
];

// The Role tab's options are the tenant's roles from the Users rail —
// shipped and custom (decision 27) — read by ShareRolePicker (L6-05). They were
// the shipped keys' literal list here, which no tenant role could appear in.

// ─── Component ─────────────────────────────────────────────────────────────

export function ShareDocumentDialog({
  open,
  onOpenChange,
  documentId,
  documentTitle,
}: ShareDocumentDialogProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AclSubjectKind>('user');
  // The Role tab only for a person who may read its options (review BF-07).
  const roleTab = useShareRoleTab();

  // Add-grant form state (one set per tab; reset on tab change)
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  // Department subject (#176). UUID of departments.id.
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [permission, setPermission] = useState<AclPermission>('view');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [userSearch, setUserSearch] = useState<string>('');

  const aclQueryKey = useMemo(
    () => ['library', 'acl', 'document', documentId],
    [documentId],
  );

  // ── Queries ─────────────────────────────────────────────────────────────

  const aclQuery = useQuery({
    queryKey: aclQueryKey,
    queryFn: () => apiClient.get<EffectiveAclResponse>(`/library/acl/documents/${documentId}`),
    enabled: open,
  });

  // `/users/search`, not `/users` (review R-01 / BF-07, B1-T2b). The roster
  // route answers the admin audience only — it returns every member's email,
  // status, department, manager, MFA state and last login — so a Member who
  // holds `share:library` and opens this dialog got a 403, react-query
  // swallowed it, and the Users tab loaded forever. That is the same failure
  // argus-live-share-dialog.tsx hit and fixed the same way (S3 P1 #8).
  // `/users/search` is the picker endpoint: the minimal `{ id, email,
  // firstName, lastName }` projection this list renders, capped at 50, scoped
  // to the caller's tenant, gated by nothing but membership.
  const usersQuery = useQuery({
    queryKey: ['users', 'search', { search: userSearch, scope: 'share-document' }],
    queryFn: () => apiClient.get<{ data: UserListItem[] }>(
      `/users/search?pageSize=50${userSearch ? `&search=${encodeURIComponent(userSearch)}` : ''}`,
    ),
    enabled: open && activeTab === 'user',
  });

  const groupsQuery = useQuery({
    queryKey: ['users', 'groups'],
    queryFn: () => apiClient.get<{ data: GroupListItem[] }>('/users/groups?pageSize=200'),
    enabled: open && activeTab === 'group',
  });

  // #176 — pull tenant departments for the new tab. useDepartments caches
  // with a 60s stale-time, so opening this dialog repeatedly does not
  // hammer the /users/departments endpoint.
  const { departments: tenantDepartments, isLoading: deptsLoading } = useDepartments({
    pageSize: 200,
  });

  // ── Mutations ───────────────────────────────────────────────────────────

  const addGrant = useMutation({
    mutationFn: async (body: {
      subjectKind: AclSubjectKind;
      subjectUserId?: string | null;
      subjectGroupId?: string | null;
      subjectRole?: string | null;
      subjectDepartmentId?: string | null;
      permission: AclPermission;
      expiresAt?: string | null;
      reason?: string | null;
    }) => {
      return apiClient.post<{ grantId: string; created: boolean }>(
        `/library/acl/documents/${documentId}/grants`,
        body,
      );
    },
    onSuccess: (result) => {
      if (result.created) {
        toast.success('Grant added');
      } else {
        toast.info('Grant already existed (no change)');
      }
      queryClient.invalidateQueries({ queryKey: aclQueryKey });
      // Reset form fields for the active tab
      setSelectedUserId('');
      setSelectedGroupId('');
      setSelectedRole('');
      setSelectedDepartmentId('');
      setPermission('view');
      setExpiresAt('');
      setReason('');
    },
    onError: (err: Error & { data?: { error?: { message?: string } } }) => {
      toast.error(getApiErrorMessage(err, 'Failed to add grant'));
    },
  });

  const removeGrant = useMutation({
    mutationFn: async (grantId: string) => {
      return apiClient.delete(`/library/acl/documents/${documentId}/grants/${grantId}`);
    },
    onSuccess: () => {
      toast.success('Grant removed');
      queryClient.invalidateQueries({ queryKey: aclQueryKey });
    },
    onError: (err: Error & { data?: { error?: { message?: string } } }) => {
      toast.error(getApiErrorMessage(err, 'Failed to remove grant'));
    },
  });

  // ── Add-grant submit ────────────────────────────────────────────────────

  const handleAddGrant = () => {
    if (activeTab === 'user') {
      if (!selectedUserId) {
        toast.error('Pick a user first');
        return;
      }
      addGrant.mutate({
        subjectKind: 'user',
        subjectUserId: selectedUserId,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    } else if (activeTab === 'group') {
      if (!selectedGroupId) {
        toast.error('Pick a group first');
        return;
      }
      addGrant.mutate({
        subjectKind: 'group',
        subjectGroupId: selectedGroupId,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    } else if (activeTab === 'role') {
      if (!selectedRole) {
        toast.error('Pick a role first');
        return;
      }
      addGrant.mutate({
        subjectKind: 'role',
        subjectRole: selectedRole,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    } else {
      // department (#176)
      if (!selectedDepartmentId) {
        toast.error('Pick a department first');
        return;
      }
      addGrant.mutate({
        subjectKind: 'department',
        subjectDepartmentId: selectedDepartmentId,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  function entryLabel(
    e: AclEntry,
    groups: GroupListItem[] | undefined,
    depts: Array<{ id: string; name: string }> | undefined,
  ): string {
    if (e.subjectKind === 'user') {
      // The server names the subject with the share (LB6-T4-R5) — never a
      // lookup in the picker's current page, which is not a directory. A
      // subject it does not name is an account outside this tenant.
      return e.subjectUser ? userPickerLabel(e.subjectUser) : 'Outside this tenant';
    }
    if (e.subjectKind === 'group') {
      const g = groups?.find((g) => g.id === e.subjectGroupId);
      return g ? g.name : e.subjectGroupId ?? 'Unknown group';
    }
    if (e.subjectKind === 'department') {
      const d = depts?.find((d) => d.id === e.subjectDepartmentId);
      return d ? `Department: ${d.name}` : `Department: ${e.subjectDepartmentId ?? 'unknown'}`;
    }
    return `Role: ${e.subjectRole ?? 'unknown'}`;
  }

  // Build an effective view that lists every direct grant first, then
  // inherited grants. We render `effective` as the source of truth so
  // the user sees "what is actually applied".
  const acl = aclQuery.data;
  const effectiveEntries = acl?.effective ?? [];

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" /> Share document
          </DialogTitle>
          <DialogDescription>
            Grant view, edit, delete, or share access on{' '}
            <span className="font-medium">{documentTitle ?? 'this document'}</span>{' '}
            to specific users, groups, or roles.
          </DialogDescription>
        </DialogHeader>

        {/* Decision 22 (B2): a share RESTRICTS — said plainly, before any
            grant is made (L6-05). It is the rule the resolver applies
            (policy/document-visibility.ts grantRestricts). */}
        <p
          className="text-sm rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-800 dark:text-amber-300"
          data-testid="share-restricts-notice"
        >
          <strong>Sharing restricts access.</strong> Once this document has any share, only the
          people, groups, roles and departments it is shared with — and the person who uploaded
          it — can see it. Everyone else loses access.
        </p>

        <div className="space-y-5 py-2">
          {/* #176 — Department-based ACL is now supported. The previous
              "not yet supported" deferral notice has been replaced with
              the current access model summary. The four axes shown here
              compose with each other: a user inherits the union of every
              matching grant. */}
          <div className="text-xs px-3 py-2 rounded-md bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20">
            <strong>Access scope.</strong> Document access is enforced by
            tenant + classification (sensitivity / export control) +
            grants you set below (user / group / role / department).
            Grants compose — a user gets access via any matching subject.
          </div>

          {/* Add-grant tabs — 4-column grid since #176 added 'department'. */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AclSubjectKind)}>
            <TabsList className={cn('w-full grid', roleTab ? 'grid-cols-4' : 'grid-cols-3')}>
              <TabsTrigger value="user">Direct (User)</TabsTrigger>
              <TabsTrigger value="group">Group</TabsTrigger>
              {roleTab && <TabsTrigger value="role">Role</TabsTrigger>}
              <TabsTrigger value="department">Department</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-3 border rounded-md p-3 bg-muted/20">
            {activeTab === 'user' && (
              <div className="space-y-2">
                <Label htmlFor="user-search">User</Label>
                <Input
                  id="user-search"
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {usersQuery.isLoading && (
                      <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                    )}
                    {usersQuery.data?.data?.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {userPickerLabel(u)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {activeTab === 'group' && (
              <div className="space-y-2">
                <Label htmlFor="group-pick">Group</Label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger id="group-pick">
                    <SelectValue placeholder="Pick a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupsQuery.isLoading && (
                      <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                    )}
                    {groupsQuery.data?.data?.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {roleTab && activeTab === 'role' && (
              <ShareRolePicker id="role-pick" value={selectedRole} onChange={setSelectedRole} />
            )}

            {activeTab === 'department' && (
              <div className="space-y-2">
                <Label htmlFor="dept-pick">Department</Label>
                <Select value={selectedDepartmentId} onValueChange={setSelectedDepartmentId}>
                  <SelectTrigger id="dept-pick">
                    <SelectValue placeholder="Pick a department" />
                  </SelectTrigger>
                  <SelectContent>
                    {deptsLoading && (
                      <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                    )}
                    {!deptsLoading && tenantDepartments.length === 0 && (
                      <SelectItem value="__empty__" disabled>
                        No departments — create one in Users → Departments first
                      </SelectItem>
                    )}
                    {tenantDepartments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Every user whose profile department matches will be granted
                  the selected permission on this document.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="perm-pick">Permission</Label>
                <Select value={permission} onValueChange={(v) => setPermission(v as AclPermission)}>
                  <SelectTrigger id="perm-pick">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERMISSIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="expires-at">Expires at (optional)</Label>
                <Input
                  id="expires-at"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Why is this access being granted?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAddGrant}
                disabled={addGrant.isPending}
              >
                {addGrant.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5 mr-2" />
                )}
                Add grant
              </Button>
            </div>
          </div>

          {/* Effective ACL panel */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Effective access</h3>
              <span className="text-xs text-muted-foreground">
                {effectiveEntries.length} grant{effectiveEntries.length === 1 ? '' : 's'}
              </span>
            </div>
            {aclQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
              </div>
            )}
            {aclQuery.isError && (
              <div className="text-sm text-destructive py-4">
                Failed to load effective ACL.
              </div>
            )}
            {!aclQuery.isLoading && !aclQuery.isError && effectiveEntries.length === 0 && (
              <div className="text-sm text-muted-foreground py-4 flex items-center gap-2">
                <Info className="h-3.5 w-3.5" />
                No grants yet. This document inherits tenant-wide visibility.
              </div>
            )}
            <ScrollArea className="max-h-64">
              <div className="space-y-1.5">
                {effectiveEntries.map((e) => {
                  const inherited = e.source === 'inherited_folder';
                  return (
                    <div
                      key={e.grantId}
                      className={cn(
                        'flex items-center justify-between gap-2 px-3 py-2 border rounded-md text-sm',
                        inherited && 'bg-muted/30',
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {entryLabel(e, groupsQuery.data?.data, tenantDepartments)}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge variant="outline" className="text-[10px]">
                            {e.permission}
                          </Badge>
                          {inherited && (
                            <Badge variant="secondary" className="text-[10px]">
                              inherited from folder
                            </Badge>
                          )}
                          {e.expiresAt && (
                            <Badge variant="outline" className="text-[10px]">
                              expires {new Date(e.expiresAt).toLocaleDateString()}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title={inherited ? 'Inherited grants must be revoked at the folder' : 'Remove grant'}
                        disabled={inherited || removeGrant.isPending}
                        onClick={() => {
                          if (inherited) return;
                          if (confirm('Remove this grant?')) {
                            removeGrant.mutate(e.grantId);
                          }
                        }}
                      >
                        {removeGrant.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-3.5 w-3.5 mr-2" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
